import React, { useCallback, useContext, useEffect, useRef } from 'react'
import { StandaloneFlightMenu } from 'components/Flights/FlightsSearch/SearchWidget/FlightSearchWidget'
import { FlightSearchWidgetActions } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import { addDays } from 'lib/dateUtils'

import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import FlightSearchWidgetDispatchContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetDispatchContext'
import { FlightsFareTypes } from 'constants/flight'
import FlightSearchMenuDesktop from './FlightSearchMenu/FlightSearchMenuDesktop'
import Spacing from 'components/Common/Spacing'
import FlightsDatePicker from './FlightsDatePicker/FlightsDatePicker'
import FlightDealDuration from 'components/Flights/FlightMerchandising/FlightDealDuration/FlightDealDuration'
import SearchFormField from 'components/SearchV2/Components/SearchFormField/SearchFormField'
import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import SearchFormFieldGroup from 'components/SearchV2/Components/SearchFormField/SearchFormFieldGroup'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { connect } from 'react-redux'
import { selectSelectedTravellerEmployees } from 'businessTraveller/selectors/businessTravellerEmployeeSelectors'
import { getBusinessTravellerSelectEnabled } from 'businessTraveller/utils/getBusinessTravellerSelectEnabled'

interface MappedStateProps {
  businessAccount?: BusinessTraveller.CurrentBusinessAccount
  travellerEmployees?: BusinessTraveller.Employee[]
}

interface Props {
  className?: string;
  anchorRef?: React.RefObject<HTMLElement>;
}

const earliestBookingDate = addDays(new Date(), 2)
const finalBookingDate = addDays(new Date(), 330)

function FlightSearchDateSelect({ className, anchorRef, businessAccount, travellerEmployees }: Props & MappedStateProps) {
  const state = useContext(FlightSearchWidgetStateContext)
  const dispatch = useContext(FlightSearchWidgetDispatchContext)

  const {
    departureAirport,
    arrivalAirport,
    checkinDate,
    checkoutDate,
    activeMenu,
    fareType,
    flightDeal,
  } = state

  const isBusinessTravellerSelectEnabled = getBusinessTravellerSelectEnabled(businessAccount)

  const setActiveMenu = useCallback((activeMenu?: StandaloneFlightMenu) => {
    dispatch({ type: FlightSearchWidgetActions.SET_ACTIVE_MENU, activeMenu })
  }, [dispatch])

  const cancelMenu = useCallback(() => {
    setActiveMenu(undefined)
  }, [setActiveMenu])

  const openMenu = useCallback(() => {
    setActiveMenu(StandaloneFlightMenu.date)
  }, [setActiveMenu])

  const onDatesChanged = useCallback((dates: { startDate: moment.Moment; endDate: moment.Moment; }) => {
    const { startDate, endDate } = dates

    if (fareType.value === FlightsFareTypes.RETURN) {
      dispatch({ type: FlightSearchWidgetActions.SET_CHECKIN_DATE, date: startDate })
      dispatch({ type: FlightSearchWidgetActions.SET_CHECKOUT_DATE, date: endDate })
      startDate && endDate && cancelMenu()
    } else {
      // one-way fare
      const departDate = endDate ?? startDate
      dispatch({ type: FlightSearchWidgetActions.SET_CHECKIN_DATE, date: departDate })
      departDate && cancelMenu()
    }

    // Trigger the open of the Select Business Traveller dropdown if a business admin
    if (isBusinessTravellerSelectEnabled && startDate && endDate && travellerEmployees?.length === 0) {
      setActiveMenu(StandaloneFlightMenu.occupants)
    }
  }, [cancelMenu, dispatch, fareType.value, isBusinessTravellerSelectEnabled, travellerEmployees, setActiveMenu])

  useEffect(() => {
    if (fareType.value === FlightsFareTypes.ONE_WAY) {
      dispatch({ type: FlightSearchWidgetActions.SET_CHECKOUT_DATE, date: null })
    }
  }, [dispatch, fareType.value])

  const triggerRef = useRef<HTMLDivElement>(null)

  return (
    <div className={className}>
      <SearchFormFieldGroup ref={triggerRef}>
        <SearchFormField
          label="Departing"
          placeholder="Select date"
          required
          requiredErrorMessage="Please select travel dates"
          value={checkinDate?.format(DMY_CASUAL_FORMAT)}
          onClick={openMenu}
        />
        {fareType.value === FlightsFareTypes.RETURN && <SearchFormField
          label="Returning"
          placeholder="Select date"
          required
          requiredErrorMessage="Please select travel dates"
          value={checkoutDate?.format(DMY_CASUAL_FORMAT)}
          onClick={openMenu}
        />}
      </SearchFormFieldGroup>
      <FlightSearchMenuDesktop
        open={activeMenu === StandaloneFlightMenu.date}
        checkinDate={checkinDate}
        checkoutDate={checkoutDate}
        onCancel={cancelMenu}
        onApply={cancelMenu}
        triggerRef={triggerRef}
        anchorRef={anchorRef}
      >
        <Spacing pt={24} pb={24} pl={32} pr={32}>
          <VerticalSpacer gap={24}>
            <FlightsDatePicker
              onDatesChange={onDatesChanged}
              startDate={checkinDate}
              endDate={checkoutDate}
              minDate={earliestBookingDate}
              maxDate={finalBookingDate}
              departureAirport={departureAirport}
              arrivalAirport={arrivalAirport}
              type="dual"
              fareType={fareType}
            />
            {flightDeal && <FlightDealDuration flightDeal={flightDeal} />}
          </VerticalSpacer>
        </Spacing>
      </FlightSearchMenuDesktop>
    </div>
  )
}

export default connect<MappedStateProps, {}, Props, App.State>((state) => {
  return {
    businessAccount: state.businessTraveller.currentBusinessAccount,
    travellerEmployees: selectSelectedTravellerEmployees(state),
  }
})(FlightSearchDateSelect)
